/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import { File } from '~/constants/file'
import { useNavigate, useParams } from 'react-router'
import {
  makeFileObsolete,
  makeMultipleFilesObsolete,
  obsoleteAllFilesInFolder,
  obsoleteAllFilesFromPropertiesSearch,
  getFileUsageById
} from '~/services/api/files'
import { useEsignature } from '~/hooks/useEsignature'
import _ from 'lodash'

interface MakeObsoleteModalProps {
  fileDetails: any
  options?: any
  onClose?: () => void
}

const MakeObsoleteModal: React.FC<MakeObsoleteModalProps> = ({ fileDetails, options = {}, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { $rootScope } = context
  const { translate } = context.service.filter
  const navigate = useNavigate()
  const params = useParams()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState(false)
  const [showUsageWarning, setShowUsageWarning] = useState(false)

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = async (): Promise<void> => {
    if (fileDetails?.id) {
      // Check if template file is being used
      if (fileDetails.systemFolderType === File.systemFolderType.TEMPLATES) {
        try {
          const data: any = await getFileUsageById(fileDetails.id)
          if (data.docTypeTplsWithTemplateFile && data.docTypeTplsWithTemplateFile.length > 0) {
            setShowUsageWarning(true)
          }
        } catch (error) {
          console.error('Failed to load file usage:', error)
        }
      }
    } else if (!isMultiselect() && !options?.selectedWholeResult) {
      doClose()
    }
  }

  const isMultiselect = (): boolean => {
    return options?.selectedIds && options?.selectedIds?.length > 1
  }

  const save = async (): Promise<void> => {
    doClose()
    let successMsg = translate('BROWSER_MAKE_OBSOLETE_SUCCESS_MSG')
    if (!isMultiselect() && fileDetails.type === File.type.FOLDER) {
      successMsg = translate('BROWSER_MAKE_OBSOLETE_FOLDER_SUCCESS_MSG')
    }

    try {
      // Get e-signature if required
      let esignature: any = {}
      if (auth.requireESignatureForFileCommands?.()) {
        esignature = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_MAKE_OBSOLETE')
      }

      // Perform obsolete operation
      if (options && options.selectedWholeResult) {
        // Whole result selection - requires special API endpoints
        if (options.isPropertiesSearch && options.meta && options.filter && options.filter.metadataCriteria) {
          // Properties search - obsolete all matching files
          const filter = { ...options.filter, workflowIsNull: true }
          await obsoleteAllFilesFromPropertiesSearch({
            propertySearchRequestDTO: { filter },
            esignatureDTO: esignature
          })
        } else if (options.parentFolderId) {
          // Folder - obsolete all files in folder
          await obsoleteAllFilesInFolder(options.parentFolderId, {
            esignatureDTO: esignature,
            filter: options.filter
          })
        }
      } else {
        // Single file or multi-selection
        const fileId = isMultiselect() ? options?.selectedIds : fileDetails.id

        if (_.isArray(fileId)) {
          // Multiple files obsolete
          await makeMultipleFilesObsolete({
            ids: fileId,
            esignature
          })
        } else {
          // Single file obsolete
          const request: any = { esignature }
          if (!_.isUndefined(options) && _.isBoolean(options.isPeriodicReview)) {
            request.prRemediationChangeReq = options.isPeriodicReview
          }
          await makeFileObsolete(fileId, request)
        }
      }

      // Success - emit events and show toast
      toast.success(successMsg)
      // Emit global events for backward compatibility (window event bus)
      window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'DELETE_FSI' }))
      window.dispatchEvent(new CustomEvent('updateDashboard'))
      window.dispatchEvent(new CustomEvent('updateSearchFiles'))
      window.dispatchEvent(new CustomEvent('updateFavorites'))
      window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
      window.dispatchEvent(new CustomEvent('fileupdate:delete', { detail: fileDetails.id }))
      window.dispatchEvent(new CustomEvent('updateReports'))

      // Navigate if current folder was obsoleted
      if (fileDetails.type === File.type.FOLDER && params.folderId && fileDetails.id === parseInt(params.folderId)) {
        const parentFolderId = fileDetails.parentFolderId
        if (parentFolderId) {
          navigate(`/browser/${parentFolderId}`)
        } else {
          navigate('/browser')
        }
      }
    } catch (error: any) {
      // Handle errors (including e-signature cancellation)
      if (error?.message !== 'E-signature cancelled') {
        console.error('Make obsolete failed:', error)
        toast.error(error?.message || error?.data?.message || 'Make obsolete failed')
      }
    }
  }

  return (
    <Modal show={isOpen} handleClose={doClose} title={translate('BROWSER_DELETE_ITEM')}>
      <Modal.Body>
        {(options?.selectedWholeResult || isMultiselect()) && (
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
            {translate('BROWSER_DELETE_CONFIRM_QUESTION_MULTIPLE_DOCS')}
          </Form.Label>
        )}
        {!options?.selectedWholeResult && !isMultiselect() && fileDetails.type && (
          <>
            <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
              {translate('BROWSER_DELETE_CONFIRM_QUESTION_SINGLE_' + fileDetails.type)}
            </Form.Label>
            <p className="whitespaceprewrap" style={{ fontSize: '12px' }}>
              {fileDetails.name}
            </p>
          </>
        )}
        {showUsageWarning && !isMultiselect() && (
          <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>{translate('FILE_IS_USED_IN_DOC_TYPE')}</Form.Label>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button data-action={'ok'} variant="link" onClick={doClose} id="e66367f6-bbe1-41ec-83ce-310caa1a8158">
          {translate('CANCEL')}
        </Button>
        <Button data-action={'ok'} className="btn btn-primary" onClick={save} id="463f38cc-3bcc-4f49-83b5-035d261bc564">
          {translate('OK')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MakeObsoleteModal
